<template>
  <div>
    <!-- Page loading indicator -->
    <PageLoading v-if="requestProcessing" />
    <div v-else>

      <v-tabs v-model="activeTab" class="mb-4">
        <v-tab class="text-none" style="letter-spacing: normal;">{{ $t('retrieveMultiModal.retrieve_sessions') }}</v-tab>
        <v-tab class="text-none" style="letter-spacing: normal;" :disabled="!isAHAdmin">{{ $t('retrieveMultiModal.retrieve_logs') }}</v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab">
        
        <!-- Tab retrieve sessions -->
        <v-tab-item>
          <div class="modal-retrieve" :class="{ 'hide-content-on-loading': requestProcessing }">
            <!-- Notice Message -->
            <div v-if="currentOption === 'yourSession' && nonRetrievableSessions.length > 0" class="warningNotice">
              <v-icon class="mr-2" style="font-size: 16px; color: red;">mdi-alert-circle</v-icon>
              <label>
                {{ $t('retrieveMultiModal.warning_1') }}<a href="#" @click="downloadDoc" style="text-decoration: underline; color: black;">{{ $t('retrieveMultiModal.warning_2') }} </a>).
              </label>
            </div>

            <div v-else class="notice">
              <v-icon class="mr-2" style="font-size: 16px;">mdi-alert-circle</v-icon>
              <label>{{ $t('retrieveMultiModal.sub_title') }}</label>
            </div>
            <div class="modal-retrieve__content">
              <!-- File Types Section -->
              <div class="columns">
                <span>{{ $t('sessionFilters.file_types') }}</span>
              </div>
              <div class="columns">
                <!-- the :key attribute will destroy and mount the modal everytime the tab changes (to have a clean state) -->
                <ModalRetrieveFileTypes
                  :key="`retrieve-file-types-${activeTab}`"
                  ref="modalRetrieveFileTypes"
                  :heliType="heliType"
                  :is-logs-mode="false"
                  @event-types-valid="valid => isFileTypesValid = valid"
                />
              </div>

              <!-- Filters Section -->
              <div class="columns">
                <label class="title-label">{{ $t('sessionFilters.filter') }}</label>
              </div>
              <!-- the :key attribute will destroy and mount the modal everytime the tab changes (to have a clean state) -->
              <ModalRetrieveFilterBy
                :key="`retrieve-filter-by-${activeTab}`"
                @event-input-valid="valid => isFilterValid = valid"
                @option-changed="opt => currentOption = opt"
                @non-retrievable-changed="sessions => nonRetrievableSessions = sessions"
                ref="modalRetrieveFilterBy"
                :heli="getHelico"
                :is-logs-mode="false"
                :multi="multi && helicopters.length > 1"
              />

            </div>
          </div>
        </v-tab-item>

        <!-- Tab retrieve logs -->
        <v-tab-item>
          <div class="modal-retrieve" :class="{ 'hide-content-on-loading': requestProcessing }">
            <div class="modal-retrieve__content">
              <!-- File Types Section -->
              <div class="columns">
                <span>{{ $t('sessionFilters.file_types') }}</span>
              </div>
              <div class="columns">
                <!-- the :key attribute will destroy and mount the modal everytime the tab changes (to have a clean state) -->
                <ModalRetrieveFileTypes
                  :key="`retrieve-logs-file-types-${activeTab}`"
                  ref="modalRetrieveFileTypes"
                  @event-types-valid="valid => isFileTypesValid = valid"
                  :heliType="heliType"
                  :is-logs-mode="true"
                />
              </div>

              <div class="columns is-vcentered mt-2 mb-2">
                <div class="column is-narrow pr-4">
                  <label class="title-label">{{ $t('sessionFilters.concatenate_files') }}</label>
                </div>

                <div class="column">
                  <div class="modal-retrieve__checkbox checkbox-holder" style="margin-bottom: 0;">
                    <input id="concat-logs" type="checkbox" v-model="concatenateFiles" />
                    <label for="concat-logs">&nbsp;</label>
                  </div>
                </div>
              </div>

              <!-- Filters Section -->
              <div class="columns">
                <label class="title-label">{{ $t('sessionFilters.filter') }}</label>
              </div>
              <!-- the :key attribute will destroy and mount the modal everytime the tab changes (to have a clean state) -->
              <ModalRetrieveFilterBy
                :key="`retrieve-logs-filter-by-${activeTab}`"
                @event-input-valid="valid => isFilterValid = valid"
                ref="modalRetrieveFilterBy"
                :heli="getHelico"
                :is-logs-mode="true"
                :multi="multi && helicopters.length > 1"
              />
            </div>
          </div>
        </v-tab-item>

      </v-tabs-items>

      <!-- Footer with Buttons -->
      <div class="footer-wrapper">
        <a @click="closeModal" class="button btn cancel-clean">{{ $t('global.button_cancel') }}</a>
        <a :disabled="!isInputValid" class="button btn validationBtn" @click="retrieveFiles()">{{ $t('global.button_retrieve') }}</a>
      </div>

    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import PageLoading from '../../views/PageLoading'
import ModalRetrieveFilterBy from './ModalRetrieveFilterBy'
import ModalRetrieveFileTypes from './ModalRetrieveFileTypes'
import axios from 'axios'
import { Other } from '../../utils/others'
import { Retrieve } from '../../utils/retrieve'
import { DateService } from '../../utils/dateService'

const TAB_SESSIONS = 0;
const TAB_LOGS     = 1;

const getDefaultTabState = () => ({
  isFilterValid: false,
  isFileTypesValid: true,
  currentOption: '',
  nonRetrievableSessions: [],
  concatenateFiles: true
});

export default {
  name: 'ModalRetrieve',
  components: {
    ModalRetrieveFilterBy,
    ModalRetrieveFileTypes,
    PageLoading
  },
  props: {
    helicopters: {
      type: Array,
      required: true
    },
    multi: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      requestProcessing: false,
      activeTab: TAB_SESSIONS,
      ...getDefaultTabState()
    }
  },
  watch: {
    activeTab() {
      // Reset the state when switching tabs.
      this.resetState();
    }
  },
  computed: {
    ...mapGetters('userStore', ['isAHAdmin']),

    isInputValid() {
      return this.isFilterValid && this.isFileTypesValid;
    },

    heliType() {
      // We assume they all have the same types
      return this.helicopters[0].type
    },

    getHelico () {
      return this.helicopters[0]
    }

  },
  methods: {
    ...mapActions('appStore', ['openModal', 'closeModal']),

    resetState() {
      Object.assign(this, getDefaultTabState());
    },

    // Retrieve sessions or logs based on selected filters and types
    retrieveFiles() {
      if (!this.isInputValid) {
        return
      }

      const formData = new FormData()
      const dataFromFilters = this.$refs.modalRetrieveFilterBy.getFiltersData()
      const dataFromTypes = this.$refs.modalRetrieveFileTypes.getTypesData()

      let byDate = false;
      let from = "";
      let to = "";
      if (dataFromFilters && dataFromFilters.option === "between_dates") {
        byDate = true;
        from = DateService.formatDateAndTimeUTC(this.$moment, dataFromFilters.from_date);
        to =  DateService.formatDateAndTimeUTC(this.$moment, dataFromFilters.to_date);
      }

      let selectedHelicopters = this.helicopters.map(h => {
        return {
          registration: h.registration,
          serial: h.serial,
          type: h.type
        }
      })

      formData.append(
        'selected_helicopters',
        JSON.stringify(selectedHelicopters)
      )

      formData.append('selected_files', dataFromTypes)

      // Add filters data to form
      for (let filter in dataFromFilters) {
        formData.append(filter, dataFromFilters[filter])
      }

      this.requestProcessing = true
      let endpoint = '/download'
      if (this.activeTab === TAB_LOGS) {
        endpoint = '/download_logs'
        formData.append('concat', this.concatenateFiles)
      }

      // Post request to retrieve files
      this.$requester
        .post(endpoint, formData)
        .then(response => {
          const downloadUrl = response.data.download_url;

          let msg = Retrieve.getDownloadSuccessMsg(this, byDate, from, to);

          if (this.activeTab == TAB_SESSIONS) {
            let incompleteSessionNumber = response.data.incompleted_sessions_number
            let SelectedSession = response.data.sessions_number

            let incompleteSessionMsg = "";
            if(incompleteSessionNumber != SelectedSession){
              incompleteSessionMsg = Retrieve.getPartialSessionMsg(this, incompleteSessionNumber, SelectedSession, byDate, from, to)
            } else {
              incompleteSessionMsg = Retrieve.getPartialSession2Msg(this, byDate, from, to)
            }

            if (incompleteSessionNumber != 0) {
              msg = incompleteSessionMsg
            }
          }

          this.$emit('downloadSuccess', {
            message: msg,
            downloadUrl: downloadUrl
          });

          //const dtrum = window.dtrum; 
          //dtrum.enterAction('Retrieve Event')
        })
        .catch(error => {
          let errMsg = this.$t('errorMsg.err')
          let errMsgApi = ""

          if (error.response) {
            const status = error.response.status;
            if (status === 404) {
              errMsg = Retrieve.getNoFilesToImportMsg(this, byDate, from, to)
            } else if (status === 504) {
              // This cannot be tested with localhost.
              errMsg = this.$t('global.nginx_timeout')
            } else {
              errMsgApi = error.response.data?.msg
            }
          }

          if (errMsgApi) {
            this.$emit('responseError', `${errMsg}\n${errMsgApi}`)
          } else {
            this.$emit('responseError', `${errMsg}`)
          }
        })
        .finally(() => {
          this.requestProcessing = false
        })
    },

    downloadDoc() {
      return Other.downloadDoc(this);
    },

  }
}
</script>

<style scoped>
/* Add custom styles here */
</style>

<style lang="scss">

.footer-wrapper {
  background-color: #e6f3f6;
  padding: 10px;
  position: absolute;
  bottom: 0;
  right: 0;
  width: 100%;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

// Fix alignment issue because of conflict between Bulma and Vuetify.
.v-date-picker-table th {
  text-align: center !important;
}

</style>
